import React, { useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useQueryClient } from "react-query";
import styles from "./SavedCardsModal.module.css";
import Modal from "components/Modal";
import Button from "Shared/Button";
import CardSettingsList from "../CardSettingsList/CardSettingsList";
import CardPaymentModel from "components/CardPaymentModel";
import { useTranslation } from "react-i18next";
import {
  usePaymentMethods,
  useUpdatePaymentMethod,
  useDeletePaymentMethod,
} from "../../hooks/usePaymentMethods";
import { useApiErrorMessage } from "../../hooks/useApiErrorMessage";

const getCardId = (card) =>
  card?.membershipCreditCardId ??
  card?.raw?.membershipCreditCardId ??
  card?.functionCreditCardId ??
  card?.raw?.functionCreditCardId ??
  card?.cardId ??
  card?.id;

// Must match the key CardSettingsList derives for the same card.
const getCardKey = (card) =>
  String(card?.id ?? card?.cardId ?? card?.membershipCreditCardId);

const buildCardList = (cards, edits, deletes, selectedKey) => {
  const deletedKeys = new Set(deletes.map(getCardKey));
  return cards
    .filter((card) => !deletedKeys.has(getCardKey(card)))
    .map((card) => {
      const key = getCardKey(card);
      const edited = { ...card, ...(edits[key] ?? {}), _cardKey: key };
      if (!selectedKey) return edited;
      const isSelected = key === selectedKey;
      return {
        ...edited,
        isDefault: isSelected,
        isPrimary: isSelected,
        isSelected,
        isChecked: isSelected,
      };
    });
};

export function SavedCardsModal({ isOpen, onClose, onSave, onCancel, onAdd }) {
  const { t } = useTranslation();
  const apiErrorMessage = useApiErrorMessage();
  const queryClient = useQueryClient();
  // Only fetch once the modal is actually visible; CartPage's own
  // usePaymentMethods() call keeps the summary card populated regardless.
  const { data: paymentCardsData } = usePaymentMethods({ enabled: isOpen });
  const paymentCards = useMemo(
    () => paymentCardsData ?? [],
    [paymentCardsData]
  );
  const updatePaymentMethod = useUpdatePaymentMethod();
  const deletePaymentMethod = useDeletePaymentMethod();
  const [cardList, setCardList] = useState([]);
  const [editingCard, setEditingCard] = useState(null);
  const [saveError, setSaveError] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const cardListRef = useRef([]);
  const selectedCardRef = useRef(null);
  // Edits and deletions are staged here and only sent to the API on Save.
  const pendingEditsRef = useRef({});
  const pendingDeletesRef = useRef([]);
  const userSelectedKeyRef = useRef(null);
  const preservePendingRef = useRef(false);

  const resetPending = () => {
    pendingEditsRef.current = {};
    pendingDeletesRef.current = [];
    userSelectedKeyRef.current = null;
  };
  const [selectedCardSnapshot, setSelectedCardSnapshot] = useState(null);

  // Any change to the modal's state invalidates a previously shown error.
  useEffect(() => {
    setSaveError("");
  }, [isOpen, paymentCards, cardList, editingCard]);

  useEffect(() => {
    if (!isOpen) {
      setCardList([]);
      cardListRef.current = [];
      selectedCardRef.current = null;
      setSelectedCardSnapshot(null);
      // Staged changes survive the trip to the Add Card modal only.
      if (preservePendingRef.current) preservePendingRef.current = false;
      else resetPending();
      return;
    }

    const selectedKey = userSelectedKeyRef.current;
    const nextList = buildCardList(
      paymentCards,
      pendingEditsRef.current,
      pendingDeletesRef.current,
      selectedKey
    );
    setCardList(nextList);
    cardListRef.current = nextList;
    const initialSelectedCard =
      nextList.find((card) =>
        selectedKey
          ? card._cardKey === selectedKey
          : card?.isDefault || card?.isPrimary
      ) ??
      nextList[0] ??
      null;
    selectedCardRef.current = initialSelectedCard;
    setSelectedCardSnapshot(initialSelectedCard);
  }, [isOpen, paymentCards]);

  useEffect(() => {
    cardListRef.current = cardList;
  }, [cardList]);

  // Staged operations always run in this order: (the primary update is sent by
  // handleSave before this), then non-primary edits, then deletions. Deletions
  // must be last because the server rejects deleting a card that is still
  // primary, and an edit or new primary selection may be what demotes it.
  const runEditsThenDeletes = async (cards) => {
    for (const editedKey of Object.keys(pendingEditsRef.current)) {
      const editedCard = cards.find((card) => card?._cardKey === editedKey);
      if (!editedCard) continue;
      const editedCardId = getCardId(editedCard);
      await updatePaymentMethod.mutateAsync({
        cardId: editedCardId,
        ...editedCard,
        membershipCreditCardId: editedCardId,
        isPrimary: Boolean(editedCard.isPrimary),
      });
      delete pendingEditsRef.current[editedKey];
    }

    for (const deletedCard of [...pendingDeletesRef.current]) {
      await deletePaymentMethod.mutateAsync(getCardId(deletedCard));
      pendingDeletesRef.current = pendingDeletesRef.current.filter(
        (card) => card !== deletedCard
      );
    }
  };

  const handleSave = async () => {
    setSaveError("");

    const currentCardList =
      cardListRef.current.length > 0 ? cardListRef.current : cardList;

    const selectedCard =
      selectedCardRef.current ??
      selectedCardSnapshot ??
      currentCardList.find((card) => card?.isDefault) ??
      currentCardList.find((card) => card?.isPrimary) ??
      currentCardList[0] ??
      null;

    if (!selectedCard) {
      setIsSaving(true);
      try {
        await runEditsThenDeletes(currentCardList);
        resetPending();
        onSave?.({ cards: currentCardList, selectedCard: null });
        onClose?.();
      } catch (err) {
        setSaveError(
          apiErrorMessage(
            err,
            t(
              "errorModal.paymentMethod",
              "We could not update your payment method. Please try again."
            )
          )
        );
      } finally {
        setIsSaving(false);
      }
      return;
    }

    const selectedMembershipCreditCardId = getCardId(selectedCard);

    if (!selectedMembershipCreditCardId) {
      setSaveError("Unable to select this card. Please choose another card.");
      return;
    }

    const selectedKey = selectedCard?._cardKey;
    const nextCardList = currentCardList.map((card) => {
      const isSelected = card?._cardKey === selectedKey;
      return {
        ...card,
        isDefault: isSelected,
        isPrimary: isSelected,
      };
    });

    setCardList(nextCardList);
    cardListRef.current = nextCardList;
    const selectedFromNextCardList =
      nextCardList.find((card) => card?._cardKey === selectedKey) ??
      selectedCard;
    selectedCardRef.current = selectedFromNextCardList;
    setSelectedCardSnapshot(selectedFromNextCardList);
    setIsSaving(true);

    try {
      // Primary first: the server refuses to delete the current primary card.
      await updatePaymentMethod.mutateAsync({
        cardId: selectedMembershipCreditCardId,
        ...selectedFromNextCardList,
        membershipCreditCardId: selectedMembershipCreditCardId,
        isPrimary: true,
      });
      delete pendingEditsRef.current[selectedKey];
      queryClient.setQueryData("paymentMethods", (prev = []) =>
        prev.map((card) => {
          const id = card?.membershipCreditCardId ?? card?.cardId ?? card?.id;
          const isSelected =
            String(id) === String(selectedMembershipCreditCardId);

          return {
            ...card,
            isPrimary: isSelected,
            isSelected,
            isChecked: isSelected,
            raw: {
              ...(card?.raw ?? {}),
              isPrimary: isSelected,
            },
          };
        })
      );

      const selectedFromNextList =
        nextCardList.find((card) => card?._cardKey === selectedKey) ??
        selectedCard;

      await runEditsThenDeletes(nextCardList);

      resetPending();
      onSave?.({ cards: nextCardList, selectedCard: selectedFromNextList });
      onClose?.();
    } catch (err) {
      setSaveError(
        apiErrorMessage(
          err,
          t(
            "errorModal.paymentMethod",
            "We could not update your payment method. Please try again."
          )
        )
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleCancel = () => {
    resetPending();
    onCancel?.();
    onClose?.();
  };

  const handleAdd = () => {
    preservePendingRef.current = true;
    onAdd?.();
  };

  const handleCardEdit = (card) => {
    if (!card) return;
    setEditingCard(card);
    setSaveError("");
  };

  const handleCardDelete = (card) => {
    setSaveError("");
    if (!getCardId(card)) {
      setSaveError("Unable to find selected card.");
      throw new Error("Unable to find selected card.");
    }

    const key = getCardKey(card);
    delete pendingEditsRef.current[key];
    pendingDeletesRef.current = [...pendingDeletesRef.current, card];
  };

  const handleEditClose = () => {
    setEditingCard(null);
  };

  const handleUpdateEditedCard = async (formData = {}) => {
    if (!getCardId(editingCard)) {
      // Thrown (not caught here) so CardPaymentModel's own submit handler
      // displays it inline, matching the styling of its other validation
      // errors instead of a separately-positioned message.
      throw new Error("Unable to find selected card.");
    }

    const editedKey = getCardKey(editingCard);
    const edit = {
      nickName: formData?.nickname ?? editingCard?.nickName,
      zipCode: formData?.postalCode ?? editingCard?.zipCode,
      expireMonth: formData?.expireMonth ?? editingCard?.expireMonth,
      expireYear: formData?.expireYear ?? editingCard?.expireYear,
    };
    pendingEditsRef.current = {
      ...pendingEditsRef.current,
      [editedKey]: edit,
    };

    const makePrimary = Boolean(formData?.isPrimary);
    if (makePrimary) userSelectedKeyRef.current = editedKey;

    const nextList = buildCardList(
      cardListRef.current,
      { [editedKey]: edit },
      [],
      makePrimary ? editedKey : null
    );
    cardListRef.current = nextList;
    setCardList(nextList);
    if (makePrimary) {
      const selected = nextList.find((card) => card._cardKey === editedKey);
      selectedCardRef.current = selected ?? null;
      setSelectedCardSnapshot(selected ?? null);
    }

    handleEditClose();
  };

  return (
    <Modal isOpen={isOpen}>
      <div className={styles.container}>
        <div className={styles.scrollArea}>
          {/* Header */}
          <div className={styles.header}>
            <div
              className={styles.addButton}
              onClick={handleAdd}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") handleAdd();
              }}
            >
              <Button
                onClick={handleAdd}
                text={t("Add Card")}
                className="closeBtn"
              />
            </div>
          </div>

          {/* Card list */}
          <CardSettingsList
            cardList={cardList}
            onCardUpdate={(updated, selectedCard = null) => {
              cardListRef.current = updated;
              const selectedKey = selectedCard?._cardKey;
              if (selectedKey) userSelectedKeyRef.current = selectedKey;
              const nextSelectedCard =
                (selectedKey
                  ? updated.find((card) => card?._cardKey === selectedKey)
                  : null) ??
                updated.find((card) => card?.isDefault || card?.isPrimary) ??
                updated[0] ??
                null;
              selectedCardRef.current = nextSelectedCard;
              setSelectedCardSnapshot(nextSelectedCard);
              setCardList(updated);
            }}
            onEditCard={handleCardEdit}
            onDeleteCard={handleCardDelete}
          />

          <Modal isOpen={Boolean(editingCard)} className={styles.editCardModal}>
            <CardPaymentModel
              onCancel={handleEditClose}
              onUpdatePayment={handleUpdateEditedCard}
              initialZipcode={editingCard?.zipCode ?? ""}
              initialNickname={editingCard?.nickName ?? ""}
              initialExpireMonth={editingCard?.expireMonth ?? ""}
              initialExpireYear={editingCard?.expireYear ?? ""}
              initialIsPrimary={Boolean(editingCard?.isPrimary)}
              initialLastFour={editingCard?.lastFour ?? ""}
              initialCardType={editingCard?.cardType ?? ""}
              metadataOnly
            />
          </Modal>

          {saveError && <div className={styles.editError}>{saveError}</div>}
        </div>

        {/* Footer */}
        <div className={styles.footer}>
          <Button
            text={t("Cancel")}
            onClick={handleCancel}
            className="closeBtn"
            disabled={isSaving}
          />
          <Button
            text={t("Save")}
            onClick={handleSave}
            className="confirmBtn"
            disabled={isSaving}
          />
        </div>
      </div>
    </Modal>
  );
}

SavedCardsModal.propTypes = {
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onSave: PropTypes.func,
  onCancel: PropTypes.func,
  onAdd: PropTypes.func,
};

export default SavedCardsModal;
